import {
  AppBar,
  SidebarDrawer,
  SidebarNavigation,
  UserMenu,
  UserMenuSubHeader,
  UserMenuToggleButton,
} from "@floriday/floriday-ui";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  useTheme,
  useMediaQuery,
  MenuList,
  styled,
  buttonBaseClasses,
  MenuItemProps,
  IconButton,
  SwipeableDrawer,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Home,
} from "@mui/icons-material";
import RfhLogo from "../RfhLogo.tsx";
import { ProductMaterialIcon, LogoutIcon } from "@floriday/floriday-icons";
import { Wc, AcUnit } from "@mui/icons-material";
import { Link, LinkProps, useRouterState, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useOktaAuth } from "@okta/okta-react";
import { getOktaAuth } from "../utils/auth.ts";
import { FloridayUIRouterCompat } from "./FloridayUIRouterCompat.tsx";

const StyledMenuItem = styled(MenuItem)<MenuItemProps & LinkProps>(
  ({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [`&.${buttonBaseClasses.root}`]: {
      fontSize: theme.typography.fontSize,
      fontWeight: theme.typography.fontWeightBold,
    },
  }),
);

const links = [
  {
    title: "Home",
    Icon: Home,
    to: "/" as const,
  },
  {
    title: "Producten",
    Icon: ProductMaterialIcon,
    to: "/products" as const,
  },
  {
    title: "ProductGroepen",
    Icon: Wc,
    to: "/cooling-regimes-product-group" as const,
  },
  {
    title: "Koelregime opties",
    Icon: AcUnit,
    to: "/cooling-regimes" as const,
  },
];

function Navigation() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { t } = useTranslation();
  const { authState } = useOktaAuth();
  const routerState = useRouterState();
  const router = useRouter();

  // Extract user name from Okta ID token claims
  const userName = authState?.idToken?.claims?.name as string | undefined || 
                   `${authState?.idToken?.claims?.given_name || ''} ${authState?.idToken?.claims?.family_name || ''}`.trim() ||
                   authState?.idToken?.claims?.email as string | undefined || 
                   '';

  const handleSignOut = async () => {
    await getOktaAuth().signOut();
  };

  const handleLogoClick = () => {
    router.navigate({ to: "/" });
  };

  const toggleDrawer = (open: boolean) => () => {
    setMenuOpen(open);
  };

  const renderMenuItems = () => {
    return links.map((link) => {
      const { pathname } = routerState.location;
      const isHome = link.to === "/";
      let isActive: boolean;

      if (isHome) {
        isActive = pathname === "/";
      } else {
        // Check for exact match first, then startsWith with delimiter
        // This prevents /cooling-regimes from matching /cooling-regimes-genus
        isActive =
          pathname === link.to ||
          pathname.startsWith(link.to + "/") ||
          pathname.startsWith(link.to + "?");
      }

      return (
        <StyledMenuItem
          key={link.title}
          component={Link}
          to={link.to}
          onClick={() => setMenuOpen(false)}
          sx={{
            color: (theme) => {
              return isActive
                ? theme.palette.primary.main
                : theme.palette.text.primary;
            },
          }}
        >
          <link.Icon sx={{ fontSize: "1.25rem" }} />
          {link.title}
        </StyledMenuItem>
      );
    });
  };

  return (
    <>
      <FloridayUIRouterCompat>
        <AppBar
          logo={
            <Box
              onClick={handleLogoClick}
              sx={{
                height: "100%",
                width: 85,
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <RfhLogo />
            </Box>
          }
          monochromeTheme
          menuButtonVisibility={isDesktop ? "never" : "always"}
          menuButtonActive={menuOpen}
          onMenuButtonClick={() => setMenuOpen(true)}
          title={t("title")}
          centerContent={
            isDesktop ? (
              <Box display="flex" alignItems="center">
                <MenuList
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    padding: 0,
                    gap: 1,
                  }}
                >
                  {renderMenuItems()}
                </MenuList>
              </Box>
            ) : undefined
          }
          rightContent={
            <Box display="flex" alignItems="center" gap={0} sx={{ width: 'auto', minWidth: 0 }}>
              {userName && (
                <UserMenuToggleButton
                  userName={userName}
                  organization=""
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  userPhotoUrl={undefined}
                  sx={{ 
                    width: 'auto !important', 
                    minWidth: '0 !important',
                    maxWidth: 'fit-content !important',
                    '& > div': {
                      width: 'auto !important',
                      minWidth: '0 !important',
                    }
                  }}
                />
              )}

              <UserMenu anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
                <UserMenuSubHeader label="Account" />
                <MenuItem divider onClick={() => void 0}>
                  <ListItemButton onClick={handleSignOut}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t("common.logout")} />
                  </ListItemButton>
                </MenuItem>
              </UserMenu>
            </Box>
          }
        />
      </FloridayUIRouterCompat>
      {!isDesktop && menuOpen && (
        <SwipeableDrawer
          anchor="left"
          open={menuOpen}
          onClose={toggleDrawer(false)}
          onOpen={toggleDrawer(true)}
          sx={{
            "& .MuiDrawer-paper": {
              backgroundColor: "#F8F8F8",
            },
          }}
        >
          <Box sx={{ width: 250, padding: 2 }}>
            <IconButton onClick={toggleDrawer(false)} sx={{ mb: 2 }}>
              <CloseIcon />
            </IconButton>
            <MenuList
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {links.map((link) => {
                const { pathname } = routerState.location;
                const isHome = link.to === "/";
                let isActive: boolean;

                if (isHome) {
                  isActive = pathname === "/";
                } else {
                  // Check for exact match first, then startsWith with delimiter
                  isActive =
                    pathname === link.to ||
                    pathname.startsWith(link.to + "/") ||
                    pathname.startsWith(link.to + "?");
                }

                return (
                  <StyledMenuItem
                    key={link.title}
                    component={Link}
                    to={link.to}
                    onClick={() => setMenuOpen(false)}
                    sx={{
                      color: (theme) => {
                        return isActive
                          ? theme.palette.primary.main
                          : theme.palette.text.primary;
                      },
                    }}
                  >
                    <link.Icon sx={{ fontSize: "1.25rem" }} />
                    {link.title}
                  </StyledMenuItem>
                );
              })}
            </MenuList>
          </Box>
        </SwipeableDrawer>
      )}
    </>
  );
}

export default Navigation;
